import { type SEOHandle } from "@nasa-gcn/remix-seo";
import {
  type HeadersFunction,
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import {
  Link,
  useFetcher,
  useLoaderData,
  useSearchParams,
} from "@remix-run/react";
import { Filter, X, XCircle } from "lucide-react";
import { useCallback, useEffect, useState, useRef } from "react";
import { serverOnly$ } from "vite-env-only/macros";

import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import FilterList from "#app/components/filter-list.tsx";
import { NormalCarListing } from "#app/components/normal-car-listing.tsx";
import { Button, buttonVariants } from "#app/components/ui/button.tsx";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#app/components/ui/select.tsx";
import { Separator } from "#app/components/ui/separator.tsx";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "#app/components/ui/sheet.tsx";
import { getUserId } from "#app/utils/auth.server.js";
import { ProviderNameSchema } from "#app/utils/connections.js";
import { prisma } from "#app/utils/db.server.ts";
import { formatState, formatText } from "#app/utils/format-text.tsx";
import { invariantResponse } from "#app/utils/misc.tsx";
import {
  combineServerTimings,
  makeTimings,
  time,
} from "#app/utils/timing.server.ts";
import { useOptionalUser } from "#app/utils/user.ts";

// Define the type for loader data, including the new fields
type LoaderData = Awaited<ReturnType<typeof loader>>;

// Define ParamsType once
interface ParamsType {
  [x: string]: any;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings("index loader"); // <-- 1. Setup Timings
  const url = new URL(request.url);

  const filters = Object.fromEntries(
    Array.from(url.searchParams.entries(), ([key, value]) => [
      decodeURIComponent(key.toLowerCase()),
      decodeURIComponent(value),
    ]),
  );

  const whereCondition: any = {};
  const orderByCondition: any = {};

  // Set orderBy filters
  if (filters.ordenar === "mais recentes") {
    orderByCondition.createdAt = "desc";
  } else if (filters.ordenar === "menor preco") {
    orderByCondition.price = "asc";
  } else {
    orderByCondition.createdAt = "desc";
  }

  if (filters.marca) whereCondition.maker = filters.marca;
  if (filters.modelo) whereCondition.model = filters.modelo;
  if (filters.localizacao) whereCondition.state = filters.localizacao;
  if (filters.versao) whereCondition.trim = filters.versao;
  if (filters.cor) whereCondition.color = filters.cor;
  if (filters.vendedor) whereCondition.sellerType = filters.vendedor;
  if (filters.condicao) whereCondition.condition = filters.condicao;
  if (filters.estado) whereCondition.state = filters.estado;
  if (filters.cidade) whereCondition.city = filters.cidade;
  whereCondition.isRemoved = false;

  // Set gte filters
  if (filters["ano-minimo"]) {
    whereCondition.modelYear = {
      ...whereCondition.modelYear,
      gte: parseInt(filters["ano-minimo"]),
    };
  }
  if (filters["preco-minimo"]) {
    whereCondition.price = {
      ...whereCondition.price,
      gte: parseInt(filters["preco-minimo"]),
    };
  }
  if (filters["quilometragem-minima"]) {
    whereCondition.odometer = {
      ...whereCondition.odometer,
      gte: parseInt(filters["quilometragem-minima"]),
    };
  }

  // Set lte filters
  if (filters["ano-maximo"]) {
    whereCondition.modelYear = {
      ...whereCondition.modelYear,
      lte: parseInt(filters["ano-maximo"]),
    };
  }
  if (filters["preco-maximo"]) {
    whereCondition.price = {
      ...whereCondition.price,
      lte: parseInt(filters["preco-maximo"]),
    };
  }
  if (filters["quilometragem-maxima"]) {
    whereCondition.odometer = {
      ...whereCondition.odometer,
      lte: parseInt(filters["quilometragem-maxima"]),
    };
  }

  // Add favorites condition to whereCondition if the filter is active
  if (filters.favoritos === "sim") {
    const userId = await getUserId(request);
    if (userId) {
      whereCondition.favorites = {
        some: {
          userId: userId,
        },
      };
    }
  } else {
    // If not filtering by favorites, ensure we don't have any leftover favorites condition
    delete whereCondition.favorites;
  }

  const page = parseInt(filters.page) || 1; // Default to page 1
  const pageSize = 24;

  let initialCarListings = await time(
    () =>
      prisma.car.findMany({
        where: whereCondition,
        orderBy: [
          {
            isPaid: "desc",
          },
          {
            isInactive: "asc",
          },
          {
            ...orderByCondition,
          },
        ],
        select: {
          id: true,
          maker: true,
          model: true,
          fabricationYear: true,
          modelYear: true,
          state: true,
          city: true,
          price: true,
          version: true,
          odometer: true,
          url: true,
          isInactive: true,
          isPaid: true,
          images: {
            select: {
              id: true,
            },
            where: {
              order: 0,
            },
          },
        },
        skip: (page - 1) * pageSize,
        take: pageSize,
      }),
    { timings, type: "Initial Car Listings loader" },
  );

  invariantResponse(initialCarListings, "initialCarListings not found", {
    status: 400,
  });

  // Determine if this is the last page
  const isLastPage = initialCarListings.length < pageSize;

  //select all unique models alphabetically, and include a where if a maker is selected
  let modelsData = await time(
    () =>
      prisma.car.findMany({
        select: {
          model: true,
        },
        orderBy: {
          model: "asc",
        },
        where: whereCondition,
        distinct: ["model"],
      }),
    { timings, type: "Models loader" },
  );

  invariantResponse(modelsData, "models not found", {
    status: 400,
  });

  const models = modelsData
    .map((model) => {
      if (model.model) {
        return {
          value: model.model,
          label: model.model.charAt(0).toUpperCase() + model.model.slice(1),
        };
      }
      return null;
    })
    .filter(Boolean);

  const citiesData = await time(
    () =>
      prisma.car.findMany({
        select: {
          city: true,
        },
        orderBy: {
          city: "asc",
        },
        where: whereCondition,
        distinct: ["city"],
      }),
    { timings, type: "Cities loader" },
  );

  invariantResponse(citiesData, "cities not found", {
    status: 400,
  });

  const cities = citiesData
    .map((city) => {
      if (city.city) {
        return {
          value: city.city,
          label: city.city.charAt(0).toUpperCase() + city.city.slice(1),
        };
      }
      return null;
    })
    .filter(Boolean);

  // Fetch unique car makers
  const makersData = await time(
    () =>
      prisma.car.findMany({
        select: {
          maker: true,
        },
        orderBy: {
          maker: "asc",
        },
        where: {
          isInactive: false,
          isRemoved: false,
        },
        distinct: ["maker"],
      }),
    { timings, type: "Makers loader" },
  );

  invariantResponse(makersData, "makers not found", {
    status: 400,
  });

  const makers = makersData
    .map((maker) => {
      if (maker.maker) {
        return {
          value: maker.maker,
          label: maker.maker.charAt(0).toUpperCase() + maker.maker.slice(1),
        };
      }
      return null;
    })
    .filter(Boolean);

  // Fetch unique states
  const statesData = await time(
    () =>
      prisma.car.findMany({
        select: {
          state: true,
        },
        orderBy: {
          state: "asc",
        },
        where: {
          isInactive: false,
          isRemoved: false,
        },
        distinct: ["state"],
      }),
    { timings, type: "States loader" },
  );

  invariantResponse(statesData, "states not found", {
    status: 400,
  });

  const states = statesData
    .map((state) => {
      if (state.state) {
        return {
          value: state.state,
          label: state.state.charAt(0).toUpperCase() + state.state.slice(1),
        };
      }
      return null;
    })
    .filter(Boolean);

  return json(
    {
      initialCarListings,
      filters,
      models,
      cities,
      makers,
      states,
      page,
      isLastPage,
    },
    {
      headers: {
        "Cache-Control": "private, max-age=3600",
        "Server-Timing": timings.toString(),
      },
    },
  );
}

export const meta: MetaFunction = ({ location }) => {
  const searchQuery = new URLSearchParams(location.search);
  let state = searchQuery.get("estado");
  let city = searchQuery.get("cidade");
  let maker = searchQuery.get("marca");

  //clean state and capitalize first letters
  if (state) {
    state = state.replace(/-/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  //clean maker and capitalize first letters
  if (maker) {
    maker = maker.replace(/-/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  //build url
  const currentUrl = `https://carregados.com.br/carros${location.search}`;

  let title = "Carros elétricos à venda";

  //build title conditionally with maker and state
  if (state && maker && city) {
    title = `Carros elétricos da ${formatText(maker)} em ${formatText(
      city,
    )}, ${formatState(state)} à venda`;
  } else if (state && city) {
    title = `Carros elétricos em ${formatText(city)}, ${formatState(
      state,
    )} à venda`;
  } else if (state && maker) {
    title = `Carros elétricos da ${formatText(maker)} em ${formatState(
      state,
    )} à venda`;
  } else if (state) {
    title = `Carros elétricos em ${formatState(state)} à venda`;
  } else if (maker) {
    title = `Carros elétricos da ${formatText(maker)} à venda`;
  }

  //build description conditionally with maker and state
  let description =
    "Explore carros elétricos à venda. Navegue pelas últimas ofertas e encontre o seu próximo carro elétrico.";

  //build description conditionally with maker, state, and city
  if (state && maker && city) {
    description = `Explore carros elétricos da ${formatText(
      maker,
    )} à venda em ${formatText(city)}, ${formatState(
      state,
    )}. Navegue pelas últimas ofertas e encontre o seu próximo carro elétrico.`;
  } else if (state && city) {
    description = `Explore carros elétricos à venda em ${formatText(
      city,
    )}, ${formatState(
      state,
    )}. Navegue pelas últimas ofertas e encontre o seu próximo carro elétrico.`;
  } else if (state && maker) {
    description = `Explore carros elétricos da ${formatText(
      maker,
    )} à venda em ${formatState(
      state,
    )}. Navegue pelas últimas ofertas e encontre o seu próximo carro elétrico.`;
  } else if (state) {
    description = `Explore carros elétricos à venda em ${formatState(
      state,
    )}. Navegue pelas últimas ofertas e encontre o seu próximo carro elétrico.`;
  }

  return [
    { title: title },
    {
      name: "description",
      content: description,
    },
    {
      itemProp: "name",
      content: title,
    },
    {
      itemProp: "description",
      content: description,
    },
    { itemProp: "image", content: "https://carregados.com.br/img/og-main.jpg" },
    { property: "og:url", content: currentUrl },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:image",
      content: "https://carregados.com.br/img/og-main.jpg",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:image",
      content: "https://carregados.com.br/img/og-main.jpg",
    },
    {
      name: "twitter:image:alt",
      content: title,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: currentUrl,
    },
  ];
};

export const handle: SEOHandle = {
  //@ts-ignore
  getSitemapEntries: serverOnly$(async (request) => {
    const locations = await prisma.car.findMany({
      select: {
        state: true,
        updatedAt: true,
      },
      orderBy: {
        updatedAt: "desc",
      },
      distinct: ["state"],
    });

    let citiesAndStates = await prisma.car.findMany({
      select: {
        state: true,
        city: true,
        updatedAt: true,
      },
      where: {
        isRemoved: false,
      },
      orderBy: {
        updatedAt: "desc",
      },
      distinct: ["state", "city"],
    });

    citiesAndStates = citiesAndStates.filter(
      (cityAndState) => cityAndState.city && cityAndState.state,
    );

    const makers = await prisma.car.findMany({
      select: {
        maker: true,
        updatedAt: true,
      },
      orderBy: {
        updatedAt: "desc",
      },
      distinct: ["maker"],
    });

    const lastUpdatedOfAllCars = await prisma.car.findMany({
      select: {
        updatedAt: true,
      },
      orderBy: {
        updatedAt: "desc",
      },
      take: 1,
    });

    return [
      ...locations.map((location) => {
        //@ts-ignore
        return {
          route: `/carros?estado=${encodeURIComponent(location.state!)}`,
          priority: 0.7,
          changefreq: "daily",
          lastmod: location.updatedAt.toISOString(),
        };
      }),
      ...makers.map((maker) => {
        //@ts-ignore
        return {
          route: `/carros?marca=${encodeURIComponent(maker.maker!)}`,
          priority: 0.7,
          changefreq: "daily",
          lastmod: maker.updatedAt.toISOString(),
        };
      }),
      ...citiesAndStates.map((cityAndState) => {
        //@ts-ignore
        return {
          route: `/carros?cidade=${encodeURIComponent(
            cityAndState.city!,
          )}&amp;estado=${encodeURIComponent(cityAndState.state!)}`,
          priority: 0.7,
          changefreq: "daily",
          lastmod: cityAndState.updatedAt.toISOString(),
        };
      }),
      {
        route: "/carros",
        priority: 0.7,
        changefreq: "daily",
        lastmod: lastUpdatedOfAllCars[0].updatedAt.toISOString(),
      },
    ];
  }),
};

export default function Carros() {
  const initialLoaderData = useLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useOptionalUser();
  const pageFetcher = useFetcher<typeof loader>();

  // Initialize with server data
  const [carListings, setCarListings] = useState(
    initialLoaderData.initialCarListings,
  );
  const [listMakers, setListMakers] = useState(initialLoaderData.makers);
  const [listModels, setListModels] = useState(initialLoaderData.models);
  const [listCities, setListCities] = useState(initialLoaderData.cities);
  const [listStates, setListStates] = useState(initialLoaderData.states);

  const [nextPageToFetch, setNextPageToFetch] = useState(2); // Start at 2 since we have page 1
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLastPage, setIsLastPage] = useState(initialLoaderData.isLastPage);
  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState<ParamsType>(initialLoaderData.filters);

  const divRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(true);

  // Update params when searchParams change
  useEffect(() => {
    const currentParams: ParamsType = {};
    searchParams.forEach((value, key) => {
      currentParams[key] = value;
    });
    setParams(currentParams);
  }, [searchParams]);

  // Handler functions
  const handleParamsChange = (e: any) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (e.target.id === "favoritos" && e.target.value === "sim") {
      const favParams = new URLSearchParams();
      favParams.set("favoritos", "sim");
      setSearchParams(favParams);
    } else {
      const paramId = e.target.id.startsWith("condicao-")
        ? "condicao"
        : e.target.id;
      newSearchParams.delete("favoritos");
      if (e.target.value !== "") {
        newSearchParams.set(paramId, e.target.value);
      } else {
        newSearchParams.delete(paramId);
      }
      setSearchParams(newSearchParams);
    }
  };

  const handleValueChange = (value: any, inputName: string) => {
    setSearchParams((prev) => {
      prev.delete("favoritos");
      if (value !== "") {
        prev.set(inputName, value);
      } else {
        prev.delete(inputName);
      }
      return prev;
    });
  };

  const handleParamsRemoval = (e: React.MouseEvent<HTMLButtonElement>) => {
    setSearchParams((prev) => {
      prev.delete(e.currentTarget.id);
      return prev;
    });
  };

  const handleOrderChange = (value: string, name: string) => {
    setSearchParams((prev) => {
      prev.set(name, value);
      return prev;
    });
  };

  // Handle filter changes and pagination
  useEffect(() => {
    // Skip the first render since we already have the data from the server
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    // Only clear listings and show loading if filters changed (not just pagination)
    const isFilterChange =
      !searchParams.has("page") || searchParams.get("page") === "1";

    if (isFilterChange) {
      setCarListings([]); // Clear only if filters changed
      setIsLastPage(false);
      setNextPageToFetch(2);
    }

    setIsLoadingMore(true);
    const pageOneParams = new URLSearchParams(searchParams);
    if (isFilterChange) {
      pageOneParams.set("page", "1");
    }
    pageFetcher.load(`/carros?${pageOneParams.toString()}`);
  }, [searchParams]);

  // Handle fetcher state changes
  useEffect(() => {
    if (pageFetcher.state === "idle" && pageFetcher.data) {
      const data = pageFetcher.data;

      // Get the filters associated with the data that was just fetched
      const fetchedFilters = data.filters;
      // Get the current filters driving the UI
      const currentFilters = params;

      // Compare fetched filters with current filters (excluding page)
      let filtersMatch = true;
      const keysToCheck = new Set([
        ...Object.keys(fetchedFilters),
        ...Object.keys(currentFilters),
      ]);
      keysToCheck.delete("page");

      for (const key of keysToCheck) {
        if (
          String(fetchedFilters[key] ?? "") !==
          String(currentFilters[key] ?? "")
        ) {
          filtersMatch = false;
          break;
        }
      }

      // Only update state if the fetched data matches current filters
      if (filtersMatch) {
        if (data.page === 1) {
          setCarListings(data.initialCarListings);
          setListModels(data.models);
          setListCities(data.cities);
        } else {
          setCarListings((prevListings) => {
            const existingIds = new Set(prevListings.map((c) => c.id));
            const newListings = data.initialCarListings.filter(
              (c: any) => !existingIds.has(c.id),
            );
            return [...prevListings, ...newListings];
          });
        }

        setIsLastPage(data.isLastPage);
        setNextPageToFetch(data.page + 1);
      }

      setIsLoadingMore(false);
    } else if (pageFetcher.state === "loading") {
      setIsLoadingMore(true);
    }
  }, [pageFetcher.state, pageFetcher.data, params]);

  // Add error handling for the fetcher
  useEffect(() => {
    if (
      pageFetcher.state === "idle" &&
      pageFetcher.data === undefined &&
      isLoadingMore
    ) {
      // If we're in a loading state but got no data, something went wrong
      setIsLoadingMore(false);
      // Optionally show an error message or retry the fetch
    }
  }, [pageFetcher.state, pageFetcher.data, isLoadingMore]);

  // Infinite scroll handler
  useEffect(() => {
    const container = divRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollThreshold = 200;
      if (
        !isLoadingMore &&
        !isLastPage &&
        window.scrollY + window.innerHeight >=
          container.offsetTop + container.offsetHeight - scrollThreshold
      ) {
        setIsLoadingMore(true);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("page", String(nextPageToFetch));
        pageFetcher.load(`/carros?${nextParams.toString()}`);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoadingMore, isLastPage, nextPageToFetch, searchParams, pageFetcher]);

  const readableKey = (key: string, value: string) => {
    const formatPrice = (price: string) => {
      const number = parseInt(price, 10);
      if (isNaN(number)) return value; // Return original value if not a number
      return `R$ ${number.toLocaleString("pt-BR")}`;
    };

    const formatMileage = (mileage: string) => {
      const number = parseInt(mileage, 10);
      if (isNaN(number)) return value; // Return original value if not a number
      return `${number.toLocaleString("pt-BR")} km`;
    };

    switch (key) {
      case "ano-minimo":
        return `Ano mínimo: ${value}`;
      case "ano-maximo":
        return `Ano máximo: ${value}`;
      case "preco-minimo":
        return `Preço mínimo: ${formatPrice(value)}`;
      case "preco-maximo":
        return `Preço máximo: ${formatPrice(value)}`;
      case "quilometragem-minima":
        return `Km mínima: ${formatMileage(value)}`;
      case "quilometragem-maxima":
        return `Km máxima: ${formatMileage(value)}`;
      case "condicao":
        return `Condição: ${formatText(value)}`;
      case "estado":
        return `Estado: ${formatState(value)}`;
      case "cidade":
        return `Cidade: ${formatText(value)}`;
      case "marca":
        return `Marca: ${formatText(value)}`;
      case "modelo":
        return `Modelo: ${formatText(value)}`;
      case "versao":
        return `Versão: ${formatText(value)}`;
      case "cor":
        return `Cor: ${formatText(value)}`;
      case "vendedor":
        return `Vendedor: ${formatText(value)}`;
      case "favoritos":
        return `Favoritos: ${value === "sim" ? "Sim" : "Não"}`;
      case "ordenar":
        return `Ordenar por: ${formatText(value)}`;
      default:
        return `${formatText(key)}: ${formatText(value)}`;
    }
  };

  return (
    <>
      <aside
        id="logo-sidebar"
        className="fixed left-0 top-0 z-40 h-screen w-64 -translate-x-full border-r bg-white pt-14 shadow-[3px_0_8px_0_rgba(0,0,0,0.1)] transition-transform sm:translate-x-0"
        aria-label="Sidebar"
      >
        <FilterList
          params={params}
          listMakers={listMakers}
          listModels={listModels}
          handleValueChange={handleValueChange}
          handleParamsChange={handleParamsChange}
          listCities={listCities}
          listStates={listStates}
          favorites={user?.favorites}
        />
      </aside>

      <div
        className={`min-h-screen bg-gray-50 pr-2 pl-2 md:pl-8 pb-8 pt-5 dark:bg-muted/30 sm:ml-64`}
      >
        <h1 className="mb-2 text-xl font-bold capitalize tracking-tight">
          {(() => {
            const brand = params.marca ? `da ${formatText(params.marca)}` : "";
            if (params.cidade && params.estado) {
              return `Carros elétricos ${brand} à venda em ${formatText(params.cidade)}, ${formatState(params.estado)}`;
            } else if (params.cidade) {
              return `Carros elétricos ${brand} à venda em ${formatText(params.cidade)}`;
            } else if (params.estado) {
              return `Carros elétricos ${brand} à venda em ${formatState(params.estado)}`;
            } else if (params.marca) {
              return `Carros elétricos ${brand} à venda`;
            } else {
              return `Carros elétricos à venda`;
            }
          })()}
        </h1>
        <Separator className="my-4 max-w-[200px]" />
        <div className="mb-4 flex w-full items-center justify-between sm:hidden">
          <div>
            <span className="text-sm font-semibold">Ordenar por:</span>
            <Select
              onValueChange={(value) => handleOrderChange(value, "ordenar")}
              name="ordenar"
              value={params.ordenar || "mais recentes"}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Mais recentes" />
              </SelectTrigger>
              <SelectContent
                ref={(ref) => {
                  if (!ref) return;
                  ref.ontouchstart = (e) => e.stopPropagation();
                  ref.ontouchmove = (e) => e.stopPropagation();
                  ref.ontouchend = (e) => e.stopPropagation();
                }}
              >
                <SelectGroup>
                  <SelectItem value="mais recentes">Mais recentes</SelectItem>
                  <SelectItem value="menor preco">Menor Preço</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="mt-[26px]">
                <Filter />
                <span className="font-bond ml-2">Filtrar</span>
              </Button>
            </SheetTrigger>
            <SheetContent className="w-5/6 px-0 overflow-y-auto">
              <div className="py-6">
                <FilterList
                  params={params}
                  listMakers={listMakers}
                  listModels={listModels}
                  handleValueChange={handleValueChange}
                  handleParamsChange={handleParamsChange}
                  listCities={listCities}
                  listStates={listStates}
                  favorites={user?.favorites}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="mb-4 hidden w-full items-center sm:flex">
          <div>
            <span className="text-sm font-semibold">Ordenar por:</span>
            <Select
              onValueChange={(value) => handleOrderChange(value, "ordenar")}
              name="ordenar"
              value={params.ordenar || "mais recentes"}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Mais recentes" />
              </SelectTrigger>
              <SelectContent
                ref={(ref) => {
                  if (!ref) return;
                  ref.ontouchstart = (e) => {
                    e.preventDefault();
                  };
                }}
              >
                <SelectGroup>
                  <SelectItem value="mais recentes">Mais recentes</SelectItem>
                  <SelectItem value="menor preco">Menor Preço</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        {Object.keys(params).filter((key) => key !== "page" && params[key])
          .length > 0 && (
          <div>
            <span className="text-sm font-semibold">Filtros:</span>
            <div className="mb-4 flex flex-wrap gap-2">
              {Object.entries(params).map(
                ([key, value]) =>
                  key !== "page" &&
                  value && (
                    <div key={key}>
                      <Button
                        id={key}
                        size="sm"
                        variant="secondary"
                        className="hover:bg-secondary-hover flex transform items-center space-x-2 rounded-full border px-3 py-1 text-sm font-medium transition-colors hover:scale-105 hover:shadow-md"
                        onClick={(e) => handleParamsRemoval(e)}
                      >
                        <span className="capitalize">
                          {readableKey(key, value)}
                        </span>
                        <XCircle
                          size={16}
                          className="ml-1 text-muted-foreground transition-transform hover:rotate-90"
                        />
                      </Button>
                    </div>
                  ),
              )}
            </div>
          </div>
        )}
        {carListings.length > 0 ? (
          <div ref={divRef} className="flex w-full flex-wrap gap-4">
            {carListings.map((carListing: any) => (
              <div key={carListing.id} className="w-full lg:max-w-xs">
                <Link
                  to={`/carros/${carListing.url}${carListing.id}`}
                  prefetch="intent"
                  className="w-full md:w-fit"
                >
                  <NormalCarListing data={carListing} user={user} />
                </Link>
              </div>
            ))}
          </div>
        ) : (
          !isLoadingMore && (
            <div className="mt-16 flex w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl font-semibold text-primary">
                Nenhum carro encontrado :(
              </span>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setSearchParams(new URLSearchParams());
                }}
                variant="secondary"
              >
                Remover Todos os Filtros
              </Button>
            </div>
          )
        )}
        {isLoadingMore && carListings.length > 0 && (
          <div className="mt-6 flex w-full items-center justify-center space-x-2 text-primary dark:text-gray-300">
            <svg
              className="h-5 w-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx={12}
                cy={12}
                r={10}
                stroke="currentColor"
                strokeWidth={4}
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="font-semibold">Carregando mais...</span>
          </div>
        )}
      </div>
    </>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
